'use client'

import * as React from 'react'
import { MapPinOff, LayoutGrid } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSearchStore, useUserStore, useSettingsStore } from '@/lib/store'
import { useI18n } from '@/lib/i18n'
import { ListingCard } from '@/components/listings/listing-card'
import { pillBtnBase } from '@/components/roostr'
import { useMapClusters } from './use-map-clusters'
import { loadGoogleMapsAPI } from '@/lib/google-maps-loader'
import { MapMetricLens, buildMetricLenses, type MetricLensId } from './map-metric-lens'
import { MapMiniCard, type MiniCardAnchor } from './map-mini-card'
import type { Listing, MapPoint } from '@/lib/types'
import type { MapFeature } from './use-map-clusters'

// ── Graceful auth failure ──────────────────────────────────────────────────
// Google Maps JS reports an invalid/expired API key exactly once by calling
// the global `gm_authFailure()`. Record it module-wide (it never fires again
// for later map instances) and notify any mounted maps so they can swap to
// the designed fallback panel instead of broken gray tiles.
let mapsAuthFailed = false
const authFailureListeners = new Set<() => void>()

function hookMapsAuthFailure() {
  if (typeof window === 'undefined') return
  const w = window as unknown as { gm_authFailure?: () => void; __roostrGmAuthHooked?: boolean }
  if (w.__roostrGmAuthHooked) return
  w.__roostrGmAuthHooked = true
  const prev = w.gm_authFailure
  w.gm_authFailure = () => {
    prev?.()
    mapsAuthFailed = true
    authFailureListeners.forEach((fn) => fn())
  }
}

interface ListingMapGoogleProps {
  /**
   * Every matching listing's coordinates. The map clusters these client-side,
   * so there is no paging: zoom out for cluster counts, zoom in until single
   * price pins remain. Fetched once per search by the parent.
   */
  points?: MapPoint[]
  /** Ids in the viewport after each pan/zoom — drives the side panel. */
  onViewportChange?: (ids: string[], countInView: number) => void
  /** Listings already hydrated by the parent (id → listing), for pin clicks. */
  hydrated?: Map<string, Listing>
  /**
   * Matches the search found that have NO coordinates and so cannot be
   * pinned. Surfaced next to the on-map count so the map never silently
   * disagrees with the results total.
   */
  unmappable?: number
  isLoadingMore?: boolean
  mode?: 'standalone' | 'split'
  onMarkerSelect?: (listing: Listing) => void
  onMarkerHover?: (listingId: string | null) => void
  selectedListingId?: string | null
  /**
   * Bumped on every panel click, even when the same listing is clicked again.
   * Without it, re-clicking a card after panning away is a no-op (the
   * selected id is unchanged, so the focus effect never re-runs).
   */
  focusNonce?: number
  /**
   * Fit the map to these points (a district). Programmatic — the panel is not
   * re-scoped by the resulting idle. Identity change triggers the fit.
   */
  fitPoints?: MapPoint[] | null
  hoveredListingId?: string | null
  /** District whose pins should glow (side-list header hover). */
  hoveredDistrict?: string | null
}

// A map point wearing a Listing's clothes. Pins label and colour themselves
// from price/area/rooms/district, all of which the skinny payload carries;
// the rest stays empty until the listing is hydrated on demand. Keeping the
// shape means the marker and click code below needs no changes.
function pointAsListing(p: MapPoint, hydrated?: Listing): Listing {
  if (hydrated) return hydrated
  return {
    id: p.id,
    title: '',
    price: p.price ?? 0,
    price_currency: p.price_currency ?? 'CZK',
    district: p.district ?? undefined,
    area: p.area ?? undefined,
    rooms: p.rooms ?? undefined,
    location: { lat: p.lat, lon: p.lon },
    keywords: [],
    images: [],
    image_tags: [],
  } as unknown as Listing
}

// Roostr price-pill pin + stone-900 cluster — design search.jsx:76-103 (MapPin)
// ported onto Google AdvancedMarkerElement HTML content. The `.listing-marker`
// / `.cluster-marker` / `.reference-marker` class names are load-bearing: the
// dark-mode tile inversion in styles/globals.css counter-inverts them.
const MAP_PIN_CSS = `
  /* Resting pin — DARK stone on purpose (user 2026-08-16: "make listing labels
     more visible for quick scanning"). The previous white pill + 1px outline
     was pixel-for-pixel Google's own POI pill ("Shell", "Barvy Adex"…), so
     listings vanished into the basemap. Dark ink on the pale map is the one
     thing the basemap never draws, and it is already this design's hover
     colour — promoted to the resting state. Slightly larger type and a
     white 2px ring so pills stay separable when they overlap. */
  .roostr-map-pin {
    position: relative;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 11px;
    border-radius: 999px;
    font-family: var(--font-mono), 'JetBrains Mono', monospace;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.1px;
    line-height: 1.2;
    white-space: nowrap;
    cursor: pointer;
    background: #1C1917;
    color: #fff;
    border: 2px solid rgba(255, 255, 255, 0.92);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.28), 0 0 0 1px rgba(0, 0, 0, 0.18);
    transition: transform 0.18s cubic-bezier(0.3, 1.3, 0.4, 1), box-shadow 0.18s, background 0.18s, color 0.18s, border-color 0.18s;
    animation: roostr-pin-in 0.4s cubic-bezier(0.3, 1.3, 0.4, 1) backwards;
  }
  /* Pointer tip so a pill reads as a MARKER anchored to a spot, not a floating
     label — the basemap's pills have no tip, which is the second cue that
     separates ours from theirs. Same colour as the pill; the ring is faked
     with a drop-shadow so it doesn't need a second pseudo-element. */
  .roostr-map-pin::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -7px;
    width: 10px;
    height: 10px;
    background: inherit;
    border-right: 2px solid rgba(255, 255, 255, 0.92);
    border-bottom: 2px solid rgba(255, 255, 255, 0.92);
    transform: translateX(-50%) rotate(45deg);
    border-radius: 0 0 2px 0;
    box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.18);
    pointer-events: none;
    z-index: -1;
  }
  .roostr-map-pin:hover { transform: scale(1.12); }
  /* Direct pointer hover + whole-district glow: now that rest is dark, the
     accent tier is a coral BORDER + lift — still clearly "this one", still
     below the focus tier's full coral fill. */
  .roostr-map-pin:hover,
  .roostr-map-pin--hot {
    border-color: rgb(var(--primary));
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.32), 0 0 0 1px rgb(var(--primary));
  }
  .roostr-map-pin__heart { display: inline-flex; color: #FCA5A5; }
  .roostr-map-pin:hover .roostr-map-pin__heart,
  .roostr-map-pin--hot .roostr-map-pin__heart { color: rgb(var(--primary)); }
  /* Focused pin — the exact listing hovered in the side list (or the selected
     one). Coral, enlarged, white-ringed, with a pulsing halo so it's
     unmistakable among the neutral pins. Declared last so it wins over
     :hover / --hot. */
  .roostr-map-pin--focus,
  .roostr-map-pin--focus:hover {
    background: rgb(var(--primary));
    color: #fff;
    border-color: #fff;
    transform: scale(1.24);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.30), 0 0 0 3px color-mix(in srgb, rgb(var(--primary)) 32%, transparent);
  }
  .roostr-map-pin--focus .roostr-map-pin__heart { color: #fff; }
  .roostr-map-pin--focus::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 999px;
    border: 2px solid rgb(var(--primary));
    animation: roostr-pin-ping 1.3s cubic-bezier(0, 0, 0.2, 1) infinite;
    pointer-events: none;
    z-index: -1;
  }
  @keyframes roostr-pin-in {
    from { transform: scale(0.9); opacity: 0; }
    to { transform: none; opacity: 1; }
  }
  @keyframes roostr-pin-ping {
    0% { transform: scale(0.9); opacity: 0.7; }
    70% { transform: scale(1.6); opacity: 0; }
    100% { transform: scale(1.6); opacity: 0; }
  }
  .roostr-map-cluster {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #1C1917;
    color: #fff;
    font-family: var(--font-mono), 'JetBrains Mono', monospace;
    font-weight: 700;
    border: 2px solid rgba(255, 255, 255, 0.28);
    box-shadow: var(--shadow-e2);
    transition: transform 0.15s cubic-bezier(0.3, 1.3, 0.4, 1), box-shadow 0.15s;
  }
  .roostr-map-cluster:hover { transform: scale(1.15); box-shadow: var(--shadow-e3); }
`

// Filled heart marking liked pins — design search.jsx:101 (11px, primary).
const HEART_SVG =
  '<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">' +
  '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>'

function createPinMarkerEl(
  listing: Listing,
  opts: { text: string; liked: boolean; hot: boolean; focus: boolean; staggerMs: number }
): HTMLDivElement {
  const el = document.createElement('div')
  el.className = 'listing-marker'
  // Generous hit area around the pill so hover doesn't flicker.
  el.style.cssText = 'cursor: pointer; padding: 8px; margin: -8px;'
  el.dataset.district = listing.district || 'Praha'

  const pill = document.createElement('button')
  pill.type = 'button'
  // Focus (single hovered/selected pin) wins over the whole-district glow.
  pill.className =
    'roostr-map-pin' + (opts.focus ? ' roostr-map-pin--focus' : opts.hot ? ' roostr-map-pin--hot' : '')
  pill.title = listing.title
  pill.style.animationDelay = `${opts.staggerMs}ms`

  if (opts.liked) {
    const heart = document.createElement('span')
    heart.className = 'roostr-map-pin__heart'
    heart.innerHTML = HEART_SVG
    pill.appendChild(heart)
  }
  pill.appendChild(document.createTextNode(opts.text))

  el.appendChild(pill)
  return el
}

function createClusterMarkerEl(pointCount: number): HTMLDivElement {
  const el = document.createElement('div')
  el.className = 'cluster-marker'
  el.style.cssText = 'cursor: pointer;'

  let size: number
  if (pointCount < 10) size = 36
  else if (pointCount < 50) size = 44
  else size = 52

  const inner = document.createElement('div')
  inner.className = 'roostr-map-cluster'
  inner.style.width = `${size}px`
  inner.style.height = `${size}px`
  inner.style.fontSize = `${size < 40 ? 12 : 13}px`
  inner.textContent = pointCount >= 1000 ? `${Math.round(pointCount / 100) / 10}k` : String(pointCount)
  el.appendChild(inner)

  return el
}

function createReferenceMarkerEl(label?: string): HTMLDivElement {
  const el = document.createElement('div')
  el.className = 'reference-marker'
  el.style.cssText = 'cursor: default; display: flex; flex-direction: column; align-items: center;'

  const pin = document.createElement('div')
  pin.style.cssText = `
    width: 32px; height: 32px; background: #ef4444; border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg); border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex; align-items: center; justify-content: center;
  `
  const inner = document.createElement('div')
  inner.style.cssText = 'transform: rotate(45deg); color: white; font-size: 14px; font-weight: bold;'
  inner.textContent = '★'
  pin.appendChild(inner)
  el.appendChild(pin)

  if (label) {
    const tag = document.createElement('div')
    tag.style.cssText = `
      margin-top: 4px; background: white; color: #1a1a1a; font-size: 11px; font-weight: 600;
      padding: 2px 6px; border-radius: 4px; box-shadow: 0 1px 4px rgba(0,0,0,0.2);
      white-space: nowrap; max-width: 160px; overflow: hidden; text-overflow: ellipsis;
    `
    tag.textContent = label
    el.appendChild(tag)
  }

  return el
}

// Designed fallback for a map that can't initialize (expired key, script
// failure): calm surface, explanation, and a route back to the grid.
function MapUnavailablePanel() {
  const { t } = useI18n()
  const { setViewMode } = useSearchStore()
  return (
    <div className="flex h-full min-h-[320px] w-full flex-col items-center justify-center gap-3 rounded-shape-lg border border-outline-variant bg-surface-low p-8">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-container">
        <MapPinOff size={28} className="text-on-primary-container" />
      </div>
      <div className="text-[16px] font-bold">{t('search.mapUnavailableTitle', 'Map is taking a break')}</div>
      <p className="max-w-[300px] text-center text-[13px] leading-snug text-ink-secondary">
        {t('search.mapUnavailableText', 'The map service is unavailable right now — browse results in the grid instead.')}
      </p>
      <button
        type="button"
        onClick={() => setViewMode('list')}
        className={cn(pillBtnBase, 'mt-1 bg-surface-high px-[18px] py-[10px] text-[13.5px]')}
      >
        <LayoutGrid size={15} />
        {t('search.backToGrid', 'Back to grid')}
      </button>
    </div>
  )
}

const MIN_MAP_HEIGHT = 200
const DEFAULT_MAP_HEIGHT = 600
const MAP_HEIGHT_KEY = 'roostr-map-height'
// Cap the prototype's i*45ms pop-in stagger (search.jsx:231) — the real map
// can hold 100 pins per viewport; an uncapped stagger would take 4.5s.
const MAX_PIN_STAGGER_MS = 540

interface HoverCardState {
  listing: Listing
  anchor: MiniCardAnchor
  containerWidth: number
  containerHeight: number
  /** Pinned (via pin click or card interaction) — survives mouse-leave. */
  pinned: boolean
}

export function ListingMapGoogle({
  points,
  onViewportChange,
  hydrated,
  unmappable = 0,
  isLoadingMore,
  mode = 'standalone',
  onMarkerSelect,
  onMarkerHover,
  selectedListingId,
  focusNonce,
  fitPoints,
  hoveredListingId,
  hoveredDistrict,
}: ListingMapGoogleProps) {
  const { t } = useI18n()
  const { results, isLoading, hiddenListings, showAllHidden, totalCount, referenceLocation } = useSearchStore()
  const { savedListingIds } = useUserStore()
  const { dealbreakers } = useSettingsStore()
  const mapContainerRef = React.useRef<HTMLDivElement>(null)
  const mapRef = React.useRef<google.maps.Map | null>(null)
  const markersRef = React.useRef<Map<string, google.maps.marker.AdvancedMarkerElement>>(new Map())
  const refMarkerRef = React.useRef<google.maps.marker.AdvancedMarkerElement | null>(null)
  const idleListenerRef = React.useRef<google.maps.MapsEventListener | null>(null)
  const [mapLoaded, setMapLoaded] = React.useState(false)
  // Bumped after every marker diff so hover/selection styling can be
  // re-applied to freshly created markers.
  const [markerTick, setMarkerTick] = React.useState(0)
  // True while the map is moving because WE moved it (panel click). The idle
  // that follows must not re-scope the side panel — only the user's own drag
  // or zoom should change which listings the panel lists.
  const programmaticMoveRef = React.useRef(false)
  // Listing whose mini-card should open once its marker exists after such a
  // move (a clustered pin has no marker to anchor to until the zoom lands).
  const pendingCardIdRef = React.useRef<string | null>(null)
  const [mapError, setMapError] = React.useState<string | null>(null)
  const [selectedListing, setSelectedListing] = React.useState<Listing | null>(null)

  // Metric lens — local to the map view; pins re-render text per lens.
  // Design search.jsx:151, 162-171.
  const [lensId, setLensId] = React.useState<MetricLensId>('price')
  const lenses = React.useMemo(() => buildMetricLenses(dealbreakers.mustHave, t), [dealbreakers.mustHave, t])
  const activeLens = lenses.find((m) => m.id === lensId) ?? lenses[0]

  // Mini-card anchored to a pin — design search.jsx:104-145. Two modes:
  // hover = transient (hides shortly after mouse-leave, with a hover bridge
  // into the card); pin click or card interaction = pinned (stays until its X,
  // an outside click, or a map move).
  const [hoverCard, setHoverCard] = React.useState<HoverCardState | null>(null)
  const hoverCardStateRef = React.useRef(hoverCard)
  hoverCardStateRef.current = hoverCard
  const miniCardWrapRef = React.useRef<HTMLDivElement | null>(null)
  const hoverLeaveTimer = React.useRef<number | null>(null)

  // Latest callbacks/hot-state via refs so marker DOM listeners and
  // updateMarkers stay stable across renders.
  const onMarkerSelectRef = React.useRef(onMarkerSelect)
  onMarkerSelectRef.current = onMarkerSelect
  // Same escape hatch as onMarkerSelect: read through a ref so updateMarkers
  // isn't re-created (and every marker torn down) when the parent re-renders.
  const onViewportChangeRef = React.useRef(onViewportChange)
  onViewportChangeRef.current = onViewportChange
  const hydratedRef = React.useRef(hydrated)
  hydratedRef.current = hydrated
  const onMarkerHoverRef = React.useRef(onMarkerHover)
  onMarkerHoverRef.current = onMarkerHover
  const hotStateRef = React.useRef<{
    selectedId: string | null
    hoveredId: string | null
    hoveredDistrict: string | null
  }>({ selectedId: null, hoveredId: null, hoveredDistrict: null })
  hotStateRef.current = {
    selectedId: selectedListingId ?? selectedListing?.id ?? null,
    hoveredId: hoveredListingId ?? null,
    hoveredDistrict: hoveredDistrict ?? null,
  }

  const cancelPinLeave = React.useCallback(() => {
    if (hoverLeaveTimer.current) {
      window.clearTimeout(hoverLeaveTimer.current)
      hoverLeaveTimer.current = null
    }
  }, [])

  const showCardForPin = React.useCallback(
    (listing: Listing, pill: HTMLElement, pinned: boolean) => {
      cancelPinLeave()
      const container = mapContainerRef.current
      if (!container) return
      const cr = container.getBoundingClientRect()
      const pr = pill.getBoundingClientRect()
      setHoverCard({
        listing,
        anchor: {
          x: pr.left + pr.width / 2 - cr.left,
          top: pr.top - cr.top,
          bottom: pr.bottom - cr.top,
        },
        containerWidth: cr.width,
        containerHeight: cr.height,
        pinned,
      })
      onMarkerHoverRef.current?.(listing.id)
    },
    [cancelPinLeave]
  )

  // Hovering ANOTHER pin takes over, even from a pinned card. Previously a
  // pinned card blocked every hover preview, so after clicking one pin the
  // rest of the map went dead — hovering other listings rendered nothing
  // (user 2026-08-16). "Pinned" now means only what it needs to: the card
  // survives leaving ITS OWN pin, so you can travel into it and click.
  const handlePinEnter = React.useCallback(
    (listing: Listing, pill: HTMLElement) => {
      const cur = hoverCardStateRef.current
      // Re-entering the pin whose card is ALREADY showing (pinned or hover
      // preview): do nothing. Rebuilding it re-ran the pop-in animation on
      // every card↔pin boundary crossing — the visible "pops up multiple
      // times" (user 2026-08-16). Just cancel any pending hide.
      if (cur && cur.listing.id === listing.id) {
        cancelPinLeave()
        return
      }
      showCardForPin(listing, pill, false)
    },
    [showCardForPin, cancelPinLeave]
  )

  const handlePinClick = React.useCallback(
    (listing: Listing, pill: HTMLElement) => {
      showCardForPin(listing, pill, true)
    },
    [showCardForPin]
  )

  const schedulePinLeave = React.useCallback(() => {
    if (hoverCardStateRef.current?.pinned) return
    cancelPinLeave()
    hoverLeaveTimer.current = window.setTimeout(() => {
      hoverLeaveTimer.current = null
      if (hoverCardStateRef.current?.pinned) return
      setHoverCard(null)
      onMarkerHoverRef.current?.(null)
    }, 320)
  }, [cancelPinLeave])

  React.useEffect(() => cancelPinLeave, [cancelPinLeave])

  // The card may have opened on a SYNTHETIC listing (a pin outside the
  // hydrated set carries only id/price/area/rooms/district — no title, photos
  // or features). Swap in the real record the moment the parent hydrates it,
  // otherwise the card sits there looking empty.
  React.useEffect(() => {
    const cur = hoverCardStateRef.current
    if (!cur) return
    const full = hydrated?.get(cur.listing.id)
    if (full && full !== cur.listing) setHoverCard({ ...cur, listing: full })
  }, [hydrated])

  const closeMiniCard = React.useCallback(() => {
    cancelPinLeave()
    setHoverCard(null)
    onMarkerHoverRef.current?.(null)
  }, [cancelPinLeave])

  // Sticky card dismissal: any pointerdown outside the card closes it. Pins
  // are exempt — their own hover/click logic owns the card's lifecycle.
  React.useEffect(() => {
    if (!hoverCard) return
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (miniCardWrapRef.current?.contains(target)) return
      if (target instanceof Element && target.closest('.roostr-map-pin')) return
      closeMiniCard()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [hoverCard, closeMiniCard])

  // Resizable map height
  const [mapHeight, setMapHeight] = React.useState(DEFAULT_MAP_HEIGHT)
  const dragState = React.useRef<{ startY: number; startH: number } | null>(null)
  const mapHeightRef = React.useRef(mapHeight)
  mapHeightRef.current = mapHeight

  // Load persisted height from localStorage
  React.useEffect(() => {
    const stored = localStorage.getItem(MAP_HEIGHT_KEY)
    if (stored) {
      const h = parseInt(stored, 10)
      if (!isNaN(h) && h >= MIN_MAP_HEIGHT) {
        setMapHeight(h)
      }
    }
  }, [])

  const handleResizeStart = React.useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    dragState.current = { startY: clientY, startH: mapHeightRef.current }
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
  }, [])

  React.useEffect(() => {
    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!dragState.current) return
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
      const delta = clientY - dragState.current.startY
      setMapHeight(Math.max(MIN_MAP_HEIGHT, dragState.current.startH + delta))
    }
    const onEnd = () => {
      if (!dragState.current) return
      dragState.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      localStorage.setItem(MAP_HEIGHT_KEY, String(mapHeightRef.current))
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onEnd)
    window.addEventListener('touchmove', onMove, { passive: false })
    window.addEventListener('touchend', onEnd)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onEnd)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
    }
  }, [])

  const hiddenCount = hiddenListings.size

  // The map's own point cloud (every match), minus anything the user hid.
  // Falls back to the shared `results` page only when no points were supplied,
  // so the component still renders standalone//bundle callers that never
  // fetched a cloud.
  const activePoints = React.useMemo<MapPoint[]>(() => {
    if (points && points.length > 0) {
      return points.filter((p) => !hiddenListings.has(p.id))
    }
    return results
      .filter((l) => l.location?.lat && l.location?.lon && !hiddenListings.has(l.id))
      .map((l) => ({
        id: l.id,
        lat: l.location!.lat,
        lon: l.location!.lon,
        price: l.price,
        price_currency: l.price_currency,
        district: l.district ?? null,
        area: l.area ?? null,
        rooms: l.rooms ?? null,
      }))
  }, [points, results, hiddenListings])

  const { getClusters, getClusterExpansionZoom, getVisibleIds, countInBounds, zoomToIsolate } =
    useMapClusters(activePoints)
  // Read inside updateMarkers (a useCallback that must not re-create per
  // points change, or every marker would be torn down and rebuilt).
  const activePointsRef = React.useRef(activePoints)
  activePointsRef.current = activePoints

  // id → point, for "bring this listing into view" lookups.
  const pointById = React.useMemo(() => {
    const m = new Map<string, MapPoint>()
    for (const p of activePoints) m.set(p.id, p)
    return m
  }, [activePoints])

  // CLICK a card in the side panel → bring that listing into view. Hover
  // deliberately does NOT move the map any more (user 2026-08-16).
  //
  // Hover-to-zoom created a feedback loop: hover → map moves → viewport
  // changes → the panel re-scopes to the new viewport → the list under the
  // cursor is replaced → the pointer now rests on a DIFFERENT card → hover
  // again. It also made the panel unscrollable (the pointer grazes rows on
  // the way past) and destroyed the user's list with no way back. Hover is
  // for correspondence only — the coral pin halo — and must never mutate the
  // data set that produced the thing being hovered.
  React.useEffect(() => {
    const map = mapRef.current
    const id = selectedListingId
    if (!map || !id || !mapLoaded) return
    const p = pointById.get(id)
    if (!p) return
    const pos = { lat: p.lat, lng: p.lon }
    const pinRendered = markersRef.current.has(`listing-${id}`)
    const zoom = map.getZoom() ?? 11

    // Everything this effect does to the map is programmatic, so the panel
    // must not re-scope from it: the user asked to see ONE listing, not to
    // throw away the list they were reading.
    programmaticMoveRef.current = true
    // Only zoom when the pin is buried in a cluster — otherwise keep the
    // user's zoom level and just centre, which preserves their sense of place.
    if (!pinRendered) map.setZoom(zoomToIsolate(id, p.lat, p.lon, zoom))
    map.panTo(pos)
    // Once the move settles and markers exist, pop this listing's card.
    pendingCardIdRef.current = id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedListingId, focusNonce, mapLoaded])

  // District focus: fit the viewport to the district's points. Marked
  // programmatic so the side panel keeps its district list instead of
  // re-scoping to whatever else the fitted viewport happens to contain.
  React.useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded || !fitPoints || fitPoints.length === 0) return
    const b = new google.maps.LatLngBounds()
    fitPoints.forEach((p) => b.extend({ lat: p.lat, lng: p.lon }))
    programmaticMoveRef.current = true
    if (fitPoints.length === 1) {
      map.panTo({ lat: fitPoints[0].lat, lng: fitPoints[0].lon })
      map.setZoom(15)
    } else {
      map.fitBounds(b, 48)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitPoints, mapLoaded])

  // Clear selected listing when component unmounts
  React.useEffect(() => {
    return () => {
      setSelectedListing(null)
    }
  }, [])

  // Swap to the fallback panel the moment Google reports an auth failure
  // (invalid/expired key) — or immediately, if it already failed earlier.
  React.useEffect(() => {
    hookMapsAuthFailure()
    const onAuthFailure = () => setMapError('Google Maps failed to authenticate (invalid or expired API key)')
    if (mapsAuthFailed) {
      onAuthFailure()
      return
    }
    authFailureListeners.add(onAuthFailure)
    return () => {
      authFailureListeners.delete(onAuthFailure)
    }
  }, [])

  // With an invalid/expired API key some Maps calls (notably the
  // AdvancedMarkerElement constructor) throw from inside Google's script.
  // Those calls run inside React effects, so an uncaught throw would unmount
  // the whole app — catch, warn once, and leave the map degraded instead.
  const markerErrorWarnedRef = React.useRef(false)
  const warnMarkerError = React.useCallback((err: unknown) => {
    if (markerErrorWarnedRef.current) return
    markerErrorWarnedRef.current = true
    console.warn('Google Maps marker rendering failed (invalid API key?):', err)
  }, [])

  // The core marker update: query supercluster for viewport, diff against existing markers
  const updateMarkers = React.useCallback(() => {
    const map = mapRef.current
    if (!map) return

    const bounds = map.getBounds()
    if (!bounds) return

    // Convert Google Maps bounds to bbox [west, south, east, north]
    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    const bbox: [number, number, number, number] = [
      sw.lng(),
      sw.lat(),
      ne.lng(),
      ne.lat(),
    ]

    const zoom = map.getZoom() || 11

    const features = getClusters(bbox, zoom)

    // Tell the parent what's in view so the side panel can follow the map —
    // but ONLY for moves the user made. A programmatic move (panel click)
    // keeps the current list intact; see the select effect above.
    if (programmaticMoveRef.current) {
      programmaticMoveRef.current = false
    } else {
      onViewportChangeRef.current?.(getVisibleIds(bbox), countInBounds(bbox))
    }

    const nextKeys = new Set<string>()

    // Build set of keys we need
    features.forEach((f: MapFeature) => {
      const key = f.type === 'cluster' ? `cluster-${f.id}` : `listing-${f.id}`
      nextKeys.add(key)
    })

    // Remove markers no longer in viewport
    const existing = markersRef.current
    existing.forEach((marker, key) => {
      if (!nextKeys.has(key)) {
        marker.map = null
        existing.delete(key)
      }
    })

    // Add new markers
    let added = 0
    try {
    features.forEach((f: MapFeature) => {
      const key = f.type === 'cluster' ? `cluster-${f.id}` : `listing-${f.id}`

      if (existing.has(key)) return // already rendered

      if (f.type === 'cluster') {
        const el = createClusterMarkerEl(f.pointCount)
        const clusterId = f.id

        el.addEventListener('click', () => {
          const expansionZoom = getClusterExpansionZoom(clusterId)
          map.panTo({ lat: f.lat, lng: f.lng })
          map.setZoom(expansionZoom)
        })

        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: { lat: f.lat, lng: f.lng },
          map,
          content: el,
        })

        existing.set(key, marker)
      } else {
        const hot = hotStateRef.current
        const isFocus = hot.selectedId === f.id || hot.hoveredId === f.id
        // Resolve the point to a Listing: the real one when the parent has
        // hydrated it, otherwise a stand-in carrying the pin's own fields.
        // For LABELLING the pin, the point's own fields are enough. For the
        // card, resolve at INTERACTION time (`live()`), not creation time:
        // markers are built once, and one built before hydration would
        // otherwise carry its synthetic shell forever — every later hover
        // re-showed a card with no photos/title until something rebuilt the
        // marker (user 2026-08-16: "pictures don't show until I click").
        const point = f.point
        const listing = pointAsListing(point, hydratedRef.current?.get(f.id))
        const live = () => pointAsListing(point, hydratedRef.current?.get(f.id))
        const isGlow = !!hot.hoveredDistrict && (listing.district || 'Praha') === hot.hoveredDistrict

        const el = createPinMarkerEl(listing, {
          text: activeLens.pinText(listing),
          liked: savedListingIds.includes(f.id),
          hot: isGlow,
          focus: isFocus,
          staggerMs: Math.min(added * 45, MAX_PIN_STAGGER_MS),
        })
        added += 1
        const pill = el.querySelector('.roostr-map-pin') as HTMLElement

        el.addEventListener('click', () => {
          if (mode === 'split') {
            // Click pins the mini-card (hover only previews it).
            handlePinClick(live(), pill)
            onMarkerSelectRef.current?.(live())
          } else {
            // Standalone opens the full card overlay — drop the preview.
            setHoverCard(null)
            setSelectedListing(live())
          }
        })

        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: { lat: f.lat, lng: f.lng },
          map,
          content: el,
        })

        el.addEventListener('mouseenter', () => {
          marker.zIndex = 120
          handlePinEnter(live(), pill)
        })
        el.addEventListener('mouseleave', () => {
          marker.zIndex = hotStateRef.current.selectedId === f.id ? 100 : 1
          schedulePinLeave()
        })

        existing.set(key, marker)
      }
    })
    } catch (err) {
      warnMarkerError(err)
    }
    // Let the hot-pin effect below re-apply focus/glow to markers that were
    // just (re)created — e.g. a hovered listing whose pin only surfaced after
    // the "bring into view" zoom.
    // A panel click asked for this listing's card; its marker exists now —
    // but Google has not POSITIONED it yet. Measuring the pill this tick
    // returns a degenerate rect at the container origin, which is why the
    // card used to appear in the map's top-left corner instead of on the pin
    // (user 2026-08-16). Wait for frames until the pill reports a real
    // on-screen box, then anchor to it.
    const pending = pendingCardIdRef.current
    if (pending) {
      pendingCardIdRef.current = null
      const anchorWhenPlaced = (attempt: number) => {
        const marker = markersRef.current.get(`listing-${pending}`)
        const pill = (marker?.content as HTMLElement | null)?.querySelector?.(
          '.roostr-map-pin'
        ) as HTMLElement | null
        const container = mapContainerRef.current
        if (!pill || !container) return
        const pr = pill.getBoundingClientRect()
        const cr = container.getBoundingClientRect()
        const placed =
          pr.width > 0 &&
          pr.height > 0 &&
          pr.left >= cr.left - 1 &&
          pr.right <= cr.right + 1 &&
          pr.top >= cr.top - 1
        if (!placed && attempt < 12) {
          requestAnimationFrame(() => anchorWhenPlaced(attempt + 1))
          return
        }
        const full = hydratedRef.current?.get(pending)
        const pt = activePointsRef.current.find((x) => x.id === pending)
        if (pt) showCardForPin(pointAsListing(pt, full), pill, true)
      }
      requestAnimationFrame(() => anchorWhenPlaced(0))
    }
    setMarkerTick((n) => n + 1)
  }, [getClusters, getClusterExpansionZoom, savedListingIds, activeLens, mode, handlePinEnter, handlePinClick, schedulePinLeave, warnMarkerError])

  // Initialize map (once)
  React.useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    let isMounted = true

    const initMap = async () => {
      try {
        await loadGoogleMapsAPI()

        if (!isMounted || !mapContainerRef.current) return

        const defaultCenter = { lat: 50.0755, lng: 14.4378 }

        const mapId = process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID'

        const map = new google.maps.Map(mapContainerRef.current, {
          center: defaultCenter,
          zoom: 11,
          mapId,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_TOP,
          },
        })

        mapRef.current = map
        setMapLoaded(true)
      } catch (err) {
        console.error('Failed to initialize Google Maps:', err)
        if (isMounted) {
          setMapError('Failed to initialize map')
        }
      }
    }

    initMap()

    return () => {
      isMounted = false
      markersRef.current.forEach(marker => {
        marker.map = null
      })
      markersRef.current.clear()

      if (refMarkerRef.current) {
        refMarkerRef.current.map = null
        refMarkerRef.current = null
      }

      if (mapRef.current) {
        mapRef.current = null
      }
    }
  }, [])

  // Listen to map move events to re-render markers
  React.useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return

    const onIdle = () => {
      // The card FOLLOWS its pin (see the bounds_changed effect below), so a
      // shifted map no longer makes it stale and it must not be dropped here.
      // Closing it on idle is what made a clicked card blink out and reappear
      // — and, because the re-open anchored once while the pan was still
      // easing, land ~30-50px off its pin, covering it (user 2026-08-16,
      // third report). Only drop a card whose pin has genuinely gone (merged
      // into a cluster, or filtered out); the follow effect handles the rest.
      const cur = hoverCardStateRef.current
      if (cur && !markersRef.current.has(`listing-${cur.listing.id}`)) setHoverCard(null)
      updateMarkers()
    }

    const listener = map.addListener('idle', onIdle)
    idleListenerRef.current = listener

    return () => {
      listener.remove()
      idleListenerRef.current = null
    }
  }, [mapLoaded, updateMarkers])

  // Keep the open card glued to its pin FOR THE WHOLE MOVE.
  //
  // The anchor used to be captured once, at click time. But clicking a pin
  // pans the map, and `panTo` EASES — so for the several hundred ms of that
  // animation the pin slides while the card stays put, sitting on top of it,
  // and it settled ~34px out of place (user 2026-08-16, third report).
  //
  // Driven by rAF, NOT by map events: Google renders the pan animation
  // internally and does not emit `bounds_changed` per frame — measured, the
  // pin swept 925px→733px while the event-driven anchor stayed frozen and
  // then jumped at the end. A frame loop is the only thing that actually
  // tracks the easing. It costs one getBoundingClientRect per frame while a
  // card is open, and the setState below is guarded so React re-renders only
  // when the pin has really moved.
  const openCardId = hoverCard?.listing.id ?? null
  React.useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded || !openCardId) return

    const reanchor = () => {
      const marker = markersRef.current.get(`listing-${openCardId}`)
      const pill = (marker?.content as HTMLElement | null)?.querySelector?.(
        '.roostr-map-pin'
      ) as HTMLElement | null
      const container = mapContainerRef.current
      if (!pill || !container) return
      const pr = pill.getBoundingClientRect()
      const cr = container.getBoundingClientRect()
      if (!pr.width || !pr.height) return
      const next = {
        x: pr.left + pr.width / 2 - cr.left,
        top: pr.top - cr.top,
        bottom: pr.bottom - cr.top,
      }
      setHoverCard((c) => {
        if (!c || c.listing.id !== openCardId) return c
        // Sub-pixel jitter would re-render on every frame for nothing.
        if (
          Math.abs(c.anchor.x - next.x) < 0.5 &&
          Math.abs(c.anchor.top - next.top) < 0.5 &&
          Math.abs(c.containerWidth - cr.width) < 0.5 &&
          Math.abs(c.containerHeight - cr.height) < 0.5
        ) {
          return c
        }
        return { ...c, anchor: next, containerWidth: cr.width, containerHeight: cr.height }
      })
    }

    let raf = 0
    const frame = () => {
      reanchor()
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
  }, [mapLoaded, openCardId, markerTick])

  // When listings change: fit bounds (include reference location)
  React.useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    // Clear all existing markers since the dataset changed
    markersRef.current.forEach((marker) => {
      marker.map = null
    })
    markersRef.current.clear()

    if (activePoints.length === 0 && !referenceLocation) return

    const bounds = new google.maps.LatLngBounds()
    activePoints.forEach((p) => {
      {
        bounds.extend({ lat: p.lat, lng: p.lon })
      }
    })

    // Include reference location in bounds so it's always visible
    if (referenceLocation) {
      bounds.extend({ lat: referenceLocation.lat, lng: referenceLocation.lon })
    }

    try {
      mapRef.current.fitBounds(bounds, 60)
    } catch (err) {
      warnMarkerError(err)
    }

    // fitBounds fires idle event which triggers updateMarkers
  }, [activePoints, referenceLocation, mapLoaded, warnMarkerError])

  // Manage reference location marker
  React.useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    // Remove previous reference marker
    if (refMarkerRef.current) {
      refMarkerRef.current.map = null
      refMarkerRef.current = null
    }

    if (!referenceLocation) return

    try {
      const el = createReferenceMarkerEl(referenceLocation.label)
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: referenceLocation.lat, lng: referenceLocation.lon },
        map: mapRef.current,
        content: el,
        zIndex: 9999,
      })

      refMarkerRef.current = marker
    } catch (err) {
      warnMarkerError(err)
    }
  }, [referenceLocation, mapLoaded, warnMarkerError])

  // Re-render visible markers when saved state or the active lens changes
  // (updateMarkers identity tracks both savedListingIds and activeLens).
  React.useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    markersRef.current.forEach((marker) => {
      marker.map = null
    })
    markersRef.current.clear()
    updateMarkers()
  }, [savedListingIds, mapLoaded, updateMarkers])

  // Hot pins: selected marker, list-hovered marker, district glow — design
  // search.jsx:77 (`hot = active || glow`).
  React.useEffect(() => {
    const effectiveSelectedId = selectedListingId ?? selectedListing?.id ?? null
    markersRef.current.forEach((marker, key) => {
      if (!key.startsWith('listing-')) return
      const id = key.slice('listing-'.length)
      const content = marker.content as HTMLElement | null
      const pill = content?.querySelector?.('.roostr-map-pin') as HTMLElement | null
      if (!pill) return

      const isSelected = id === effectiveSelectedId
      const isHovered = id === hoveredListingId
      const focus = isSelected || isHovered
      const glow = !!hoveredDistrict && content?.dataset.district === hoveredDistrict

      // Single focused pin gets the coral halo; whole-district glow stays dark.
      pill.classList.toggle('roostr-map-pin--focus', focus)
      pill.classList.toggle('roostr-map-pin--hot', glow && !focus)
      marker.zIndex = isSelected ? 100 : isHovered ? 90 : glow ? 50 : 1
    })
  }, [selectedListingId, selectedListing, hoveredListingId, hoveredDistrict, results.length, mapLoaded, markerTick])

  // Designed fallback when the map can't initialize (missing/invalid/expired
  // key, script load failure). The split-view side list keeps working — only
  // the map area degrades.
  if (mapError) {
    return (
      <div
        className={mode === 'split' ? 'h-full w-full' : 'w-full'}
        style={mode === 'split' ? undefined : { height: mapHeight }}
      >
        <MapUnavailablePanel />
      </div>
    )
  }

  // Shared overlay chrome: frosted metric switcher (top-left), listing count
  // pill under it, lens hint (bottom-left, above Google's attribution).
  const lensOverlays = mapLoaded && !isLoading && (
    <>
      <MapMetricLens
        lenses={lenses}
        value={activeLens.id}
        onChange={setLensId}
        className="absolute left-3 top-3 z-10"
      />
      <div
        className="absolute left-3 top-[52px] z-10 rounded-full border border-outline-variant bg-surface/[0.88] px-3 py-1 shadow-e1 backdrop-blur-[8px]"
        title={
          unmappable > 0
            ? t('search.unmappableHint', '{n} matching listings have no location and cannot be shown on the map').replace('{n}', String(unmappable))
            : undefined
        }
      >
        <p className="text-xs font-medium">
          {activePoints.length} {t('search.onMap', 'on map')}
          {unmappable > 0 && (
            <span className="text-ink-muted">
              {' · '}
              {t('search.unmappable', '{n} without location').replace('{n}', String(unmappable))}
            </span>
          )}
        </p>
      </div>
      <div className="pointer-events-none absolute bottom-[26px] left-3.5 z-10 font-mono text-[10.5px] uppercase tracking-[1px] text-ink-muted">
        {activeLens.hint}
      </div>
    </>
  )

  const miniCard = hoverCard && (
    <div
      ref={miniCardWrapRef}
      // Hover bridge: entering the card keeps a transient preview alive;
      // leaving it schedules the hide (no-ops when pinned).
      onMouseEnter={cancelPinLeave}
      onMouseLeave={schedulePinLeave}
      // Interacting with the card (paging photos, liking) pins it so it can't
      // vanish mid-use.
      onPointerDown={() => setHoverCard((c) => (c && !c.pinned ? { ...c, pinned: true } : c))}
    >
      <MapMiniCard
        listing={hoverCard.listing}
        lens={activeLens}
        anchor={hoverCard.anchor}
        containerWidth={hoverCard.containerWidth}
        containerHeight={hoverCard.containerHeight}
        onOpen={() => window.open(`/listings/${hoverCard.listing.id}`, '_blank', 'noopener,noreferrer')}
        onClose={closeMiniCard}
      />
    </div>
  )

  // No pager any more. The map holds every match at once and clusters them, so
  // panning and zooming IS the navigation — the old page controls (and the
  // value-anchored jump chips that only existed to skip between 100-listing
  // pages) have nothing left to page through.
  const mapPager = null

  if (mode === 'split') {
    return (
      <div className="relative w-full h-full">
        <div ref={mapContainerRef} className="w-full h-full" data-testid="map-container" />
        {isLoading && (
          <div className="absolute inset-0 bg-background/90 flex items-center justify-center z-20">
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-muted-foreground">Loading listings...</p>
            </div>
          </div>
        )}
        {lensOverlays}
        {mapPager}
        {!isLoading && hiddenCount > 0 && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full border border-border shadow-sm text-sm">
              <span className="text-muted-foreground">{hiddenCount} hidden</span>
              <button onClick={showAllHidden} className="font-medium text-primary hover:underline">Show all</button>
            </div>
          </div>
        )}
        {miniCard}
        <style>{MAP_PIN_CSS}</style>
      </div>
    )
  }

  return (
    <div className="w-full">
    <div
      className="relative w-full rounded-xl overflow-hidden border border-border"
      style={{ height: mapHeight }}
    >
      {/* Map container */}
      <div
        ref={mapContainerRef}
        className="w-full h-full"
        data-testid="map-container"
      />

      {/* Loading overlay - don't unmount the map */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/90 flex items-center justify-center z-20">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-muted-foreground">Loading listings...</p>
          </div>
        </div>
      )}

      {/* Metric switcher, count pill, lens hint */}
      {lensOverlays}

      {/* Map pagination */}
      {mapPager}

      {/* Hidden listings banner */}
      {!isLoading && hiddenCount > 0 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 max-w-md w-full px-4">
          <div className="flex items-center justify-between px-2 py-2 bg-muted rounded-lg border border-border shadow-sm">
            <span className="text-sm text-muted-foreground">
              {hiddenCount} listing{hiddenCount > 1 ? 's' : ''} hidden
            </span>
            <button
              onClick={showAllHidden}
              className="text-sm font-medium text-primary hover:underline"
            >
              Show all
            </button>
          </div>
        </div>
      )}

      {/* Hover mini-card */}
      {miniCard}

      {/* Selected Listing Card */}
      {selectedListing && (
        <div className="absolute left-4 right-4 top-16 bottom-4 pointer-events-none z-20">
          <div className="relative h-full w-full max-w-md pointer-events-auto">
            {/* Close button */}
            <button
              onClick={() => setSelectedListing(null)}
              className="absolute -top-2 -right-2 z-30 p-2 rounded-full bg-background border-2 border-border shadow-lg hover:bg-muted transition-colors"
              title="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Scrollable card container */}
            <div className="h-full overflow-y-auto overflow-x-hidden rounded-xl shadow-2xl bg-background">
              <ListingCard listing={selectedListing} variant="map" />
            </div>
          </div>
        </div>
      )}
      <style>{MAP_PIN_CSS}</style>
    </div>

    {/* Resize drag handle */}
    <div
      onMouseDown={handleResizeStart}
      onTouchStart={handleResizeStart}
      className="flex items-center justify-center h-5 cursor-row-resize group select-none"
    >
      <div className="w-10 h-1 rounded-full bg-border group-hover:bg-muted-foreground/60 transition-colors" />
    </div>
    </div>
  )
}
